import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Shield,
  Heart,
  Sparkles,
  Users,
  Calendar,
  Clock,
  Video,
  CheckCircle2,
  ArrowRight,
  ArrowDown,
  ChevronRight,
  ChevronLeft,
  Circle,
  Minus,
  Mail,
  MessageCircle
} from 'lucide-react';

const CursoLimites = () => {
  const [activeSection, setActiveSection] = useState('hero');

  // Navigation items for secondary nav
  const navItems = [
    { id: 'hero', label: 'Inicio' },
    { id: 'que-es', label: 'El curso' },
    { id: 'para-quien', label: '¿Para quién?' },
    { id: 'formato', label: 'Formato' },
    { id: 'contenido', label: 'Contenido' },
    { id: 'precio', label: 'Reservar' }
  ];

  // Scroll spy effect
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 180;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 140;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  // Color palette aligned with school identity
  // Primary dark: #8a7428 (golden dark - aligned with school logo)
  // Accent gold: hsl(42, 45%, 55%) / #c5a059
  // Sage: hsl(85, 15%, 60%)
  // Sage light: hsl(80, 15%, 96%)

  return (
    <>
      <Helmet>
        <title>LÍMITES: el espacio original de la consciencia | Escuela de Consciencia Akáshica</title>
        <meta name="description" content="Master-curso online en directo para recuperar tu espacio interno, sostener tu eje y relacionarte sin culpa, sin dureza y sin perder el corazón. 8 lunes desde el 18 de mayo. 14 plazas." />
        <meta property="og:title" content="LÍMITES: el espacio original de la consciencia" />
        <meta property="og:description" content="Un master-curso para volver a habitar tu espacio interno y poner límites sin culpa. Online en directo. 14 plazas." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://res.cloudinary.com/dpppkdhdk/image/upload/v1776874282/landing_limites_oxeqbv.png" />
      </Helmet>

      {/* Secondary Navigation Bar - Sticky below main navbar */}
      <div className="sticky top-[72px] z-40 bg-white/95 backdrop-blur-md border-b border-[hsl(var(--border))] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Back to formations */}
            <Link 
              to="/formaciones" 
              className="flex items-center gap-1 text-sm text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Formaciones</span>
            </Link>

            {/* Course nav - scrollable on mobile */}
            <nav className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
              {navItems.slice(1).map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors rounded-full whitespace-nowrap ${
                    activeSection === item.id
                      ? 'text-[hsl(var(--text-primary))] bg-[#eef0f2]'
                      : 'text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] hover:bg-[#eef0f2]/50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* CTA */}
            <button
              onClick={() => scrollToSection('precio')}
              data-testid="sticky-nav-reservar-btn"
              className="hidden sm:flex items-center gap-2 bg-[#c9a227] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#b89220] transition-all shadow-md"
            >
              Reservar mi plaza
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 sm:hidden bg-white border-t border-[hsl(var(--border))] p-3 shadow-2xl">
        <button
          onClick={() => scrollToSection('precio')}
          data-testid="mobile-sticky-reservar-btn"
          className="w-full bg-[#c9a227] text-white py-4 rounded-full text-base font-medium hover:bg-[#b89220] transition-all flex items-center justify-center gap-2 shadow-lg"
        >
          Reservar mi plaza
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <main className="bg-white pb-20 sm:pb-0">
        {/* ==================== HERO ==================== */}
        <section id="hero" className="relative min-h-[90vh] flex items-center overflow-hidden">
          {/* Background image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto,w_1800/v1776874282/landing_limites_oxeqbv.png)`,
            }}
          >
            {/* Cool blue/slate gradient overlay — matches existing dark sections */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2332]/95 via-[#2d3748]/85 to-[#4a5568]/40" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-24 w-full">
            <div className="max-w-3xl">
              {/* Badges */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 animate-fade-in">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-xs sm:text-sm font-medium">
                  <Video className="w-3.5 h-3.5" />
                  Online en directo
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm text-white/90 rounded-full text-xs sm:text-sm font-medium">
                  <Calendar className="w-3.5 h-3.5" />
                  8 lunes · 17:30–20:30
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#c9a227]/20 backdrop-blur-sm text-[#e8c75a] rounded-full text-xs sm:text-sm font-medium">
                  <Users className="w-3.5 h-3.5" />
                  14 plazas · Empieza 18 mayo
                </span>
              </div>

              {/* Title */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading text-white tracking-tight mb-3 sm:mb-4 animate-slide-up leading-[1.05]">
                LÍMITES
              </h1>
              <p className="text-xl sm:text-2xl lg:text-3xl font-heading text-[#e8c75a] mb-8 sm:mb-10 animate-slide-up stagger-1 italic">
                el espacio original de la consciencia
              </p>

              {/* Description */}
              <p className="text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl mb-10 sm:mb-12 animate-slide-up stagger-2">
                Un master-curso online en directo para recuperar tu espacio interno, sostener tu eje y relacionarte sin culpa, sin dureza y sin perder el corazón.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-3 sm:gap-4 animate-slide-up stagger-3">
                <button
                  onClick={() => scrollToSection('precio')}
                  className="w-full sm:w-auto bg-[#c9a227] hover:bg-[#b89220] text-white px-8 py-4 rounded-full text-base font-medium transition-all hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 shadow-lg"
                  data-testid="hero-reservar-btn"
                >
                  Reservar mi plaza
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollToSection('formato')}
                  className="w-full sm:w-auto border border-white/30 hover:bg-white/10 text-white px-8 py-4 rounded-full text-base font-medium transition-all inline-flex items-center justify-center gap-2 backdrop-blur-sm"
                >
                  Ver fechas y precio
                  <ArrowDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== FRASE DESTACADA ==================== */}
        <section className="py-14 sm:py-16 bg-[#f7f8fa] border-y border-[#d4d8dc]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-xl sm:text-2xl lg:text-3xl font-heading text-[hsl(var(--text-primary))] italic leading-relaxed">
              Antes de poner un límite hacia fuera,<br className="hidden sm:block" /> necesitas volver a sentir dónde estás tú.
            </p>
          </div>
        </section>

        {/* ==================== ¿QUÉ ES EL ESPACIO ORIGINAL? ==================== */}
        <section className="py-20 sm:py-28 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-10 sm:mb-12">
              ¿Qué es el espacio original de la consciencia?
            </h2>
            <div className="space-y-5 text-base sm:text-lg text-[hsl(var(--text-secondary))] leading-relaxed">
              <p>
                Es ese lugar interno desde el que puedes habitarte antes de adaptarte.
              </p>
              <p>
                Es el espacio donde sigues estando contigo incluso cuando el otro necesita, pide, demanda, se enfada, se aleja o no te comprende.
              </p>
              <p className="text-[hsl(var(--text-primary))] font-heading italic text-lg sm:text-xl">
                Desde ahí puedes amar sin fusionarte, acompañar sin absorber, escuchar sin desaparecer, dar sin vaciarte y comunicar sin atacar.
              </p>
            </div>
          </div>
        </section>


        {/* ==================== SECCIÓN 2: "Este no es un curso..." ==================== */}
        <section id="no-es" className="py-20 sm:py-28 bg-[#f5f0e6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-8 sm:mb-12">
              Este no es un curso para aprender a decir "no"
            </h2>
            
            <p className="text-base sm:text-lg text-[hsl(var(--text-secondary))] text-center mb-10 sm:mb-14">
              Es un espacio para recordar algo más profundo:
            </p>

            {/* Quote */}
            <blockquote className="relative bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 shadow-soft border border-[hsl(var(--border))]/30 mb-12 sm:mb-16">
              <div className="absolute -top-4 left-8 sm:left-12 w-8 h-8 bg-[hsl(var(--accent))]/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
              </div>
              <p className="text-lg sm:text-xl lg:text-2xl font-heading text-[hsl(var(--text-primary))] text-center italic leading-relaxed">
                "El límite sano aparece cuando la consciencia vuelve a reconocer su espacio."
              </p>
            </blockquote>

            <div className="space-y-6 text-[hsl(var(--text-secondary))] leading-relaxed">
              <p className="text-base sm:text-lg">
                Muchas personas saben qué deberían hacer… pero no logran sostenerlo en el cuerpo ni en la vida real.
              </p>
              
              <p className="text-base sm:text-lg text-[#9a8a6a] italic">
                Se adaptan. Se contienen. Se dan de más. Se explican demasiado.
                <br className="hidden sm:block" />
                Y un día, sin entender por qué, se sienten agotadas, cerradas o desconectadas de sí mismas.
              </p>

              <p className="text-base sm:text-lg font-medium text-[hsl(var(--text-primary))]">
                No porque no sepan poner límites, sino porque aprendieron demasiado pronto a salir de su propio espacio para conservar amor, vínculo o seguridad.
              </p>

              <div className="bg-white/60 rounded-2xl p-6 sm:p-8 mt-8 border border-[hsl(var(--border))]/20">
                <p className="text-sm sm:text-base text-[hsl(var(--text-secondary))] mb-4">Cuando eso ocurre, el límite:</p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {[
                    'llega tarde',
                    'aparece desde la rabia o el colapso',
                    'genera culpa',
                    'o rompe el vínculo'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-[hsl(var(--text-secondary))]">
                      <Minus className="w-4 h-4 text-[#9a8a6a] flex-shrink-0" />
                      <span className="text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-base sm:text-lg text-[hsl(var(--text-primary))] font-medium pt-4">
                Este curso nace para reordenar eso desde la raíz.
              </p>
            </div>
          </div>
        </section>

        {/* ==================== SECCIÓN 3: "¿De qué va realmente...?" ==================== */}
        <section id="que-es" className="py-20 sm:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-8 sm:mb-12">
              ¿De qué va realmente este curso?
            </h2>

            <div className="space-y-6 text-[hsl(var(--text-secondary))] leading-relaxed mb-12">
              <p className="text-base sm:text-lg">
                No vamos a trabajar el límite como técnica. Ni como estrategia mental. Ni como escudo defensivo.
              </p>
              <p className="text-base sm:text-lg">
                Vamos a trabajar el límite como lo que es en su origen: <strong className="text-[hsl(var(--text-primary))]">una función natural del cuerpo, del sistema nervioso y del campo de consciencia.</strong>
              </p>
            </div>

            {/* Features */}
            <div className="bg-gradient-to-br from-white to-white/60 rounded-2xl sm:rounded-3xl p-6 sm:p-10 mb-10 border border-[#e8d9b8]/40 shadow-sm">
              <p className="text-base sm:text-lg text-[hsl(var(--text-primary))] font-medium mb-6">Un límite que:</p>
              <div className="grid gap-3 sm:gap-4">
                {[
                  'no necesita justificarse',
                  'no nace del miedo',
                  'no genera culpa',
                  'no endurece',
                  'no rompe el vínculo',
                  'no te separa de tu corazón'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 sm:gap-4">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--accent))]" />
                    </div>
                    <span className="text-base sm:text-lg text-[hsl(var(--text-primary))]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-lg sm:text-xl text-center font-heading text-[hsl(var(--text-primary))] italic">
              Aquí el límite no se impone. Se habita.
            </p>
          </div>
        </section>

        {/* ==================== SECCIÓN 4: "Enfoque enraizado" ==================== */}
        <section className="py-20 sm:py-28 bg-gradient-to-br from-[#4a5568] to-[#2d3748] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-white text-center mb-8 sm:mb-12">
              Un enfoque profundamente enraizado
            </h2>

            <p className="text-base sm:text-lg text-white/70 text-center mb-10 sm:mb-14">
              Este curso integra:
            </p>

            {/* Pillars */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16">
              {[
                { icon: Heart, title: 'Regulación del sistema nervioso' },
                { icon: Circle, title: 'Cuerpo y percepción somática' },
                { icon: Sparkles, title: 'Campo energético y presencia' },
                { icon: Users, title: 'Vínculo, comunicación y vida cotidiana' },
                { icon: Shield, title: 'Consciencia sin bypass espiritual' }
              ].map((pillar, i) => (
                <div 
                  key={i} 
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <pillar.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[hsl(var(--accent))] mb-3" strokeWidth={1.5} />
                  <p className="text-sm sm:text-base text-white/90 font-medium">{pillar.title}</p>
                </div>
              ))}
            </div>

            <p className="text-white/60 text-center mb-10 text-sm sm:text-base">
              No trabajamos desde la mente sola. Ni desde la espiritualidad desconectada del cuerpo.
            </p>

            {/* Quote */}
            <blockquote className="text-center">
              <p className="text-xl sm:text-2xl lg:text-3xl font-heading text-white italic leading-relaxed">
                "Cuando el cuerpo está protegido, el alma puede permanecer abierta."
              </p>
            </blockquote>
          </div>
        </section>

        {/* ==================== SECCIÓN 5: "¿Para quién es?" ==================== */}
        <section id="para-quien" className="py-20 sm:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-10 sm:mb-16">
              ¿Para quién es este curso?
            </h2>

            <div className="bg-[#eef0f2] rounded-2xl sm:rounded-3xl p-6 sm:p-10 mb-8">
              <p className="text-base sm:text-lg text-[hsl(var(--text-primary))] font-medium mb-6">Este espacio es para ti si:</p>
              <div className="grid gap-3 sm:gap-4">
                {[
                  'te cuesta sostener límites sin sentir culpa',
                  'te adaptas más de lo que quieres',
                  'te agotas en vínculos o ayudando a otros',
                  'das más de lo que recibes',
                  'has confundido amor con disponibilidad',
                  'has usado la espiritualidad para aguantar de más',
                  'quieres seguir siendo sensible y amorosa/o, sin perderte'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 sm:gap-4">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[hsl(var(--accent))]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-[hsl(var(--accent))]" />
                    </div>
                    <span className="text-sm sm:text-base text-[hsl(var(--text-primary))]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Not for */}
            <div className="text-center bg-gradient-to-br from-[hsl(var(--accent))]/10 to-[hsl(var(--accent))]/5 rounded-2xl p-6 sm:p-8 border border-[hsl(var(--accent))]/20">
              <p className="text-base sm:text-lg text-[hsl(var(--text-primary))]">
                <span className="font-medium">No es un curso para "arreglarte".</span>
                <br />
                <span className="text-[hsl(var(--text-primary))] font-heading text-lg sm:text-xl">Es un curso para dejar de abandonarte.</span>
              </p>
            </div>
          </div>
        </section>

        {/* ==================== SECCIÓN 6: "Formato y fechas" ==================== */}
        <section id="formato" className="py-20 sm:py-28 bg-[#eef0f2]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-10 sm:mb-16">
              Formato y fechas
            </h2>

            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
              {[
                { icon: Calendar, label: 'Duración', value: '2 meses' },
                { icon: Calendar, label: 'Fechas', value: '18 de mayo – 6 de julio' },
                { icon: Clock, label: 'Día y horario', value: 'Lunes 17:30–20:30 (hora España)' },
                { icon: Video, label: 'Modalidad', value: 'Online · en directo' },
                { icon: Users, label: 'Plazas', value: '14 personas' }
              ].map((item, i) => (
                <div key={i} className="card-soft">
                  <div className="flex items-center gap-3 mb-2">
                    <item.icon className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                    <span className="text-sm text-[hsl(var(--text-tertiary))]">{item.label}</span>
                  </div>
                  <p className="text-base sm:text-lg font-medium text-[hsl(var(--text-primary))]">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Includes */}
            <div className="card-soft">
              <p className="text-sm sm:text-base text-[hsl(var(--text-tertiary))] mb-4">Incluye:</p>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {[
                  '8 sesiones en vivo',
                  'Prácticas guiadas',
                  'Audios de integración',
                  'Acompañamiento grupal cuidado'
                ].map((item, i) => (
                  <span key={i} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#eef0f2] text-[hsl(var(--text-secondary))] rounded-full text-xs sm:text-sm">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ==================== SECCIÓN 7: "Qué trabajaremos" ==================== */}
        <section id="contenido" className="py-20 sm:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-6 sm:mb-8">
              Qué trabajaremos
            </h2>

            <p className="text-base sm:text-lg text-[hsl(var(--text-secondary))] text-center mb-10 sm:mb-14 max-w-2xl mx-auto">
              A lo largo del proceso iremos recorriendo, de forma progresiva y muy práctica:
            </p>

            <div className="grid gap-3 sm:gap-4">
              {[
                { num: '01', title: 'El origen del colapso del límite' },
                { num: '02', title: 'La protección original como regreso al propio espacio' },
                { num: '03', title: 'El límite como función del sistema nervioso' },
                { num: '04', title: 'El campo propio y las fugas de energía' },
                { num: '05', title: 'La culpa, la autoexigencia y el rol salvador' },
                { num: '06', title: 'La comunicación clara sin dureza' },
                { num: '07', title: 'La espiritualidad encarnada (sin bypass)' },
                { num: '08', title: 'El límite integrado como estado natural' }
              ].map((module, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-4 sm:gap-6 bg-[#eef0f2] hover:bg-[#eef0f2]/70 rounded-xl sm:rounded-2xl p-4 sm:p-6 transition-colors group"
                >
                  <span className="text-2xl sm:text-3xl font-heading text-[#9a8a6a] group-hover:text-[hsl(var(--accent))] transition-colors">
                    {module.num}
                  </span>
                  <span className="text-base sm:text-lg text-[hsl(var(--text-primary))] font-medium">{module.title}</span>
                </div>
              ))}
            </div>

            <p className="text-sm sm:text-base text-[hsl(var(--text-tertiary))] text-center mt-10 max-w-2xl mx-auto">
              Todo aplicado a la vida real: pareja, familia, trabajo, vínculos, espiritualidad y relación contigo.
            </p>
          </div>
        </section>

        {/* ==================== SECCIÓN 8: "Precio y plazas" ==================== */}
        <section id="precio" className="py-20 sm:py-28 bg-[#f5f0e6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] text-center mb-6 sm:mb-8">
              Reserva tu plaza
            </h2>

            <p className="text-base sm:text-lg text-[hsl(var(--text-secondary))] text-center mb-10 sm:mb-12 max-w-2xl mx-auto">
              Grupo reducido de 14 personas. El acceso se confirma con la reserva.
            </p>

            {/* 8 fechas concretas */}
            <div className="bg-white rounded-2xl border border-[#d4d8dc] p-6 sm:p-8 mb-8 sm:mb-10">
              <p className="text-xs sm:text-sm uppercase tracking-[0.12em] text-[#9a8a6a] font-semibold text-center mb-5">
                Calendario · Lunes 17:30–20:30 (hora España)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { l: '18', m: 'May' },
                  { l: '25', m: 'May' },
                  { l: '01', m: 'Jun' },
                  { l: '08', m: 'Jun' },
                  { l: '15', m: 'Jun' },
                  { l: '22', m: 'Jun' },
                  { l: '29', m: 'Jun' },
                  { l: '06', m: 'Jul' },
                ].map((d, i) => (
                  <div key={i} className="flex flex-col items-center justify-center bg-[#fdfaf0] rounded-xl py-4 border border-[#e8d9b8]/50">
                    <span className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] leading-none">{d.l}</span>
                    <span className="text-[11px] uppercase tracking-wide text-[#9a8a6a] mt-1">{d.m} 2026</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing cards */}
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
              {/* Single payment */}
              <div className="card-soft border-2 border-[hsl(var(--accent))] relative">
                <div className="absolute -top-3 left-6 bg-[hsl(var(--accent))] text-white text-xs font-medium px-3 py-1 rounded-full">
                  Recomendado
                </div>
                <p className="text-sm text-[hsl(var(--text-tertiary))] mb-2">Pago único</p>
                <p className="text-4xl sm:text-5xl font-heading text-[hsl(var(--text-primary))] mb-4">420 €</p>
                <p className="text-sm text-[hsl(var(--text-tertiary))] mb-6">Acceso completo al curso</p>
                <Link
                  to="/checkout/limites"
                  className="btn-gold w-full inline-flex items-center justify-center gap-2"
                  data-testid="cta-pago-unico"
                >
                  Reservar mi plaza
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Two payments */}
              <div className="card-soft">
                <p className="text-sm text-[hsl(var(--text-tertiary))] mb-2">2 pagos</p>
                <p className="text-4xl sm:text-5xl font-heading text-[hsl(var(--text-primary))] mb-1">2 × 230 €</p>
                <p className="text-sm text-[#9a8a6a] mb-4">Total: 460 € · Segundo cobro automático a los 30 días</p>
                <p className="text-sm text-[hsl(var(--text-tertiary))] mb-6">Acceso completo al curso</p>
                <Link
                  to="/checkout/limites?mode=installment"
                  className="btn-outline w-full inline-flex items-center justify-center gap-2"
                  data-testid="cta-pago-fraccionado"
                >
                  Reservar pago fraccionado
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="text-[11px] text-[hsl(var(--text-tertiary))] mt-2 text-center">Primera cuota hoy · segunda en 30 días (automático vía PayPal)</p>
              </div>
            </div>

            {/* Bizum / métodos alternativos */}
            <div className="bg-white border border-[#e8d9b8]/60 rounded-xl p-5 sm:p-6 mb-10 shadow-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-sm sm:text-base font-medium text-[hsl(var(--text-primary))] mb-1">¿Prefieres pagar por Bizum?</p>
                  <p className="text-sm text-[hsl(var(--text-tertiary))]">
                    Sin problema. Escríbeme por WhatsApp o email y te paso el número Bizum + concepto. Disponible tanto para pago único (420 €) como fraccionado (2 × 230 €).
                  </p>
                </div>
                <a
                  href="https://wa.me/34654827313?text=Hola%20Herminio%2C%20me%20gustar%C3%ADa%20reservar%20mi%20plaza%20del%20curso%20L%C3%ADMITES%20y%20pagar%20por%20Bizum."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-[hsl(var(--accent))] hover:bg-[#b89220] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                  data-testid="cta-bizum-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  Pedir por WhatsApp
                </a>
              </div>
            </div>

            {/* Plazas */}
            <div className="text-center bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/20 rounded-xl p-4 sm:p-5 mb-10">
              <p className="text-sm sm:text-base text-[hsl(var(--text-primary))]">
                <strong>Plazas disponibles: 14</strong>
              </p>
            </div>

            {/* WhatsApp dudas */}
            <div className="text-center">
              <p className="text-sm text-[hsl(var(--text-tertiary))] mb-3">¿Tienes dudas antes de reservar?</p>
              <a
                href="https://wa.me/34654827313?text=Hola%20Herminio%2C%20tengo%20dudas%20sobre%20el%20curso%20L%C3%ADMITES."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#9a8a6a] hover:text-[hsl(var(--accent))] transition-colors text-sm font-medium"
                data-testid="cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                Hablar conmigo por WhatsApp
              </a>
            </div>

            <p className="text-base sm:text-lg text-[hsl(var(--text-primary))] font-heading italic text-center mt-12">
              No se trata de estar preparada/o. Se trata de estar disponible.
            </p>
          </div>
        </section>

        {/* Sección de "Solicitud de acceso" eliminada — flujo ahora va directo al checkout PayPal o pago fraccionado por email */}

        {/* ==================== SECCIÓN 10: "Cierre" ==================== */}
        <section className="py-20 sm:py-28 bg-gradient-to-br from-[#4a5568] to-[#2d3748] text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading text-white mb-8 sm:mb-12">
              Volver a casa
            </h2>

            <p className="text-base sm:text-lg text-white/70 mb-10 sm:mb-14">
              Este curso no busca que te cierres más. Busca que vuelvas a casa dentro de ti.
            </p>

            {/* Final quote */}
            <blockquote className="mb-12 sm:mb-16">
              <p className="text-xl sm:text-2xl lg:text-3xl font-heading text-white italic leading-relaxed">
                "El límite no separa. Protege la vida para que el amor pueda permanecer."
              </p>
            </blockquote>

            <button
              onClick={() => scrollToSection('precio')}
              data-testid="final-cta-reservar-btn"
              className="bg-white text-[#8a7428] px-8 py-4 rounded-full text-base font-medium hover:bg-white/90 transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              Reservar mi plaza
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      </main>

      {/* Custom CSS for hiding scrollbar */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default CursoLimites;
